// src/pages/Settings.jsx
import React, { useState, useEffect } from "react";
import Hero from "../../components/ui/Hero.jsx";
import Card from "../../components/ui/Card.jsx";
import {
  Mail,
  Bell,
  Cog,
  Globe,
  Clock,
  Users,
  Loader2,
  Shield,
} from "lucide-react";
import { getNotificationSettings, updateNotificationSettings } from "../../api/lenderPortal/notifications.js";
import {
  fetchTwoFaStatus,
  beginTwoFaSetup,
  confirmTwoFaSetup,
  disableTwoFa,
} from "../../api/authorTwoFa.js";
import toast from "react-hot-toast";

/*************************
 * Settings Page
 *************************/

const cx = (...s) => s.filter(Boolean).join(" ");

function Toggle({ enabled, onChange, label, id }) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      className={cx(
        enabled ? "bg-sky-500" : "bg-gray-300",
        "relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
      )}
      aria-label={label}
    >
      <span
        className={cx(
          enabled ? "translate-x-5" : "translate-x-0",
          "pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
        )}
      />
    </button>
  );
}

export default function Settings() {
  const THEME_STORAGE_KEY = "ds_theme_preference";
  /* ---------- Preferences ---------- */
  const [language, setLanguage] = useState("en-GB");
  const [timezone, setTimezone] = useState("Europe/London");
  const [theme, setTheme] = useState("light"); // draft selection in UI
  const [appliedTheme, setAppliedTheme] = useState("light"); // active theme used by page

  /* ---------- Notifications (new items + email routing) ---------- */
  // In-app toggles
  const [notifOthersUploads, setNotifOthersUploads] = useState(true);
  const [notifAppProgress, setNotifAppProgress] = useState(true);
  const [notifMyUploadAndAnalytics, setNotifMyUploadAndAnalytics] = useState(true);
  const [notifCompliance, setNotifCompliance] = useState(true);
  const [notifSecurity, setNotifSecurity] = useState(true);

  // Email delivery
  const [emailNotify, setEmailNotify] = useState(true);
  const [emailPM, setEmailPM] = useState("product.manager@yourbank.com");
  const [emailBDMField, setEmailBDMField] = useState("");
  const [emailBDMOffice, setEmailBDMOffice] = useState("");
  const [sendToPM, setSendToPM] = useState(true); // default ON
  const [sendToBDMField, setSendToBDMField] = useState(false);
  const [sendToBDMOffice, setSendToBDMOffice] = useState(false);

  // Loading state
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [twoFaEmailEnabled, setTwoFaEmailEnabled] = useState(false);
  const [twoFaEmail, setTwoFaEmail] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaVerified, setTwoFaVerified] = useState(false);
  const [setupTmpId, setSetupTmpId] = useState("");
  const [setupMethod, setSetupMethod] = useState("email");
  const [twoFaPendingAction, setTwoFaPendingAction] = useState(null);
  const [twoFaBusy, setTwoFaBusy] = useState(false);
  const [twoFaMsg, setTwoFaMsg] = useState("");

  /* ---------- Handlers ---------- */

  // Load notification settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoadingSettings(true);
        const data = await getNotificationSettings();
        
        // Map backend response to frontend state
        // Adjust field names based on actual backend response structure
        if (data) {
          if (data.inApp || data.in_app) {
            const inApp = data.inApp || data.in_app || {};
            setNotifOthersUploads(inApp.othersUploads ?? inApp.others_uploads ?? true);
            setNotifAppProgress(inApp.applicationProgress ?? inApp.application_progress ?? true);
            setNotifMyUploadAndAnalytics(inApp.myUploadAndAnalytics ?? inApp.my_upload_and_analytics ?? true);
            setNotifCompliance(inApp.compliance ?? true);
            setNotifSecurity(inApp.security ?? true);
          }
          
          if (data.email) {
            setEmailNotify(data.email.enabled ?? true);
            if (data.email.recipients) {
              const recipients = data.email.recipients;
              if (recipients.productManager || recipients.product_manager) {
                setEmailPM(recipients.productManager || recipients.product_manager || "");
                setSendToPM(true);
              }
              if (recipients.fieldBDM || recipients.field_bdm) {
                setEmailBDMField(recipients.fieldBDM || recipients.field_bdm || "");
                setSendToBDMField(!!(recipients.fieldBDM || recipients.field_bdm));
              }
              if (recipients.officeBDM || recipients.office_bdm) {
                setEmailBDMOffice(recipients.officeBDM || recipients.office_bdm || "");
                setSendToBDMOffice(!!(recipients.officeBDM || recipients.office_bdm));
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to load notification settings:", error);
        // Don't show error toast on initial load - settings might not exist yet
      } finally {
        setLoadingSettings(false);
      }
    };
    
    loadSettings();
  }, []);

  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (savedTheme === "light" || savedTheme === "dark" || savedTheme === "system") {
        setTheme(savedTheme);
        setAppliedTheme(savedTheme);
      }
    } catch {
      // ignore localStorage failures
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    const root = document.documentElement;
    const media = window.matchMedia("(prefers-color-scheme: dark)");

    const applyTheme = () => {
      const resolved = theme === "system" ? (media.matches ? "dark" : "light") : theme;
      root.classList.toggle("dark", resolved === "dark");
      root.setAttribute("data-theme", resolved);
      root.style.colorScheme = resolved;
    };

    applyTheme();
    const onSystemThemeChange = () => {
      if (theme === "system") applyTheme();
    };
    media.addEventListener("change", onSystemThemeChange);
    return () => media.removeEventListener("change", onSystemThemeChange);
  }, [appliedTheme]);

  useEffect(() => {
    (async () => {
      try {
        const status = await fetchTwoFaStatus();
        if (!status?.ok) return;
        setTwoFaEmailEnabled(status.enabled);
        if (status.user) setTwoFaEmail(status.user);
        setTwoFaVerified(status.enabled);
      } catch {
        /* status unavailable */
      }
    })();
  }, []);

  const handleSaveNotifications = async () => {
    try {
      setSavingSettings(true);
      const payload = {
        inApp: {
          othersUploads: notifOthersUploads,
          applicationProgress: notifAppProgress,
          myUploadAndAnalytics: notifMyUploadAndAnalytics,
          compliance: notifCompliance,
          security: notifSecurity,
        },
        email: {
          enabled: emailNotify,
          recipients: {
            productManager: sendToPM ? emailPM : null,
            fieldBDM: sendToBDMField ? emailBDMField : null,
            officeBDM: sendToBDMOffice ? emailBDMOffice : null,
          },
        },
      };
      
      await updateNotificationSettings(payload);
      toast.success("Notification preferences saved successfully");
    } catch (error) {
      console.error("Failed to save notification settings:", error);
      toast.error("Failed to save notification preferences");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSavePreferences = () => {
    try {
      localStorage.setItem("ds_language_preference", language);
      localStorage.setItem("ds_timezone_preference", timezone);
      localStorage.setItem(THEME_STORAGE_KEY, theme);
      setAppliedTheme(theme);
      toast.success("Preferences saved");
    } catch {
      toast.error("Failed to save preferences");
    }
  };
  const sendTwoFaCode = async (action = twoFaPendingAction) => {
    const isDisable = action === "disable";
    setTwoFaBusy(true);
    setTwoFaMsg("");
    if (!isDisable) setTwoFaVerified(false);
    try {
      const res = await beginTwoFaSetup("email");
      if (!res.tmpId) {
        throw new Error("Unable to start 2FA setup. Missing temporary ID from server.");
      }
      setSetupTmpId(res.tmpId);
      setSetupMethod(res.method || "email");
      if (res.user) setTwoFaEmail(res.user);
      const em = res.user || twoFaEmail || "your email";
      setTwoFaMsg(
        isDisable
          ? `We sent a confirmation code to ${em}. Enter it below to turn off email 2FA.`
          : res.message || `Verification code sent to ${em}. Enter it below to enable email 2FA.`
      );
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to send code.";
      toast.error(typeof msg === "string" ? msg : "Failed to send code.");
    } finally {
      setTwoFaBusy(false);
    }
  };

  const verifyTwoFaCode = async () => {
    if (!setupTmpId) {
      toast.error("Please send a verification code first.");
      return;
    }
    if (!twoFaCode.trim()) {
      toast.error("Please enter the verification code.");
      return;
    }
    setTwoFaBusy(true);
    setTwoFaMsg("");
    try {
      const isDisable = twoFaPendingAction === "disable";
      const res = isDisable
        ? await disableTwoFa({ tmp_id: setupTmpId, otp: twoFaCode, method: setupMethod })
        : await confirmTwoFaSetup({ tmp_id: setupTmpId, otp: twoFaCode, method: setupMethod });

      if (isDisable) {
        if (res.ok !== false) {
          setTwoFaEmailEnabled(false);
          setTwoFaVerified(false);
          setTwoFaMsg("Email two-factor authentication is now turned off.");
          setSetupTmpId("");
          setTwoFaCode("");
          setTwoFaPendingAction(null);
          toast.success("Email 2FA disabled");
        }
      } else if (res.enabled) {
        setTwoFaEmailEnabled(true);
        setTwoFaVerified(true);
        setTwoFaMsg("Email two-factor authentication is now enabled.");
        setSetupTmpId("");
        setTwoFaCode("");
        setTwoFaPendingAction(null);
        toast.success("Email 2FA enabled");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Invalid code.";
      toast.error(typeof msg === "string" ? msg : "Invalid code.");
    } finally {
      setTwoFaBusy(false);
    }
  };

  const onTwoFaToggle = async () => {
    if (twoFaBusy) return;

    if (twoFaEmailEnabled && twoFaVerified) {
      setTwoFaPendingAction("disable");
      setTwoFaCode("");
      setTwoFaMsg("");
      await sendTwoFaCode("disable");
      return;
    }

    if (twoFaEmailEnabled) return;

    setTwoFaPendingAction("enable");
    setTwoFaEmailEnabled(true);
    setTwoFaVerified(false);
    setTwoFaCode("");
    await sendTwoFaCode("enable");
  };

  const isDisableFlow = twoFaPendingAction === "disable";
  const is2faActive = twoFaEmailEnabled && twoFaVerified && !setupTmpId;

  return (
    <>
      <Hero title="Settings" subtitle="Manage your lender portal preferences" />

      <div className="grid gap-4 sm:gap-6 lg:grid-cols-2 mt-4">
        {/* Notifications */}
        <Card className="p-4 sm:p-6">
          <div className="mb-4 flex items-center gap-2">
            <Bell className="h-5 w-5 text-sky-500" />
            <h2 className="text-xl font-semibold">Notifications</h2>
          </div>

          <div className="space-y-6">
            {/* NEW — Other lenders' product uploads */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Products uploaded by other lenders</p>
                <p className="text-sm text-gray-600">
                  Be alerted when competitors publish or update products.
                </p>
              </div>
              <Toggle
                id="notifOthersUploads"
                label="Other lenders uploads"
                enabled={notifOthersUploads}
                onChange={setNotifOthersUploads}
              />
            </div>

            {/* NEW — Application progress against your product */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Application progress (against your products)</p>
                <p className="text-sm text-gray-600">
                  Key milestones such as submitted, valuation, legals and completion.
                </p>
              </div>
              <Toggle
                id="notifAppProgress"
                label="Application progress"
                enabled={notifAppProgress}
                onChange={setNotifAppProgress}
              />
            </div>

            {/* NEW — Upload & performance analytics */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Product upload & performance analytics</p>
                <p className="text-sm text-gray-600">
                  Confirmations on upload plus periodic engagement and performance insights.
                </p>
              </div>
              <Toggle
                id="notifMyUploadAndAnalytics"
                label="Upload & analytics"
                enabled={notifMyUploadAndAnalytics}
                onChange={setNotifMyUploadAndAnalytics}
              />
            </div>

            {/* Compliance / Security (kept) */}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Compliance & regulatory</p>
                <p className="text-sm text-gray-600">
                  Audits, regulatory changes, or actions required for products.
                </p>
              </div>
              <Toggle
                id="notifCompliance"
                label="Compliance"
                enabled={notifCompliance}
                onChange={setNotifCompliance}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">User access & security alerts</p>
                <p className="text-sm text-gray-600">
                  Permission changes, suspicious logins, or security incidents.
                </p>
              </div>
              <Toggle
                id="notifSecurity"
                label="Security alerts"
                enabled={notifSecurity}
                onChange={setNotifSecurity}
              />
            </div>

            {/* Email delivery settings */}
            <div className="mt-2 rounded-xl border p-4">
              <div className="mb-3 flex items-center gap-2">
                <Mail className="h-5 w-5 text-sky-500" />
                <div className="font-semibold">Email Notifications</div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Receive notifications by email</p>
                  <p className="text-sm text-gray-600">Send important alerts to team members.</p>
                </div>
                <Toggle
                  id="emailNotify"
                  label="Email notifications"
                  enabled={emailNotify}
                  onChange={setEmailNotify}
                />
              </div>

              <div className={cx("mt-4 grid gap-4", emailNotify ? "opacity-100" : "opacity-50 pointer-events-none")}>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-sky-500" />
                  <div className="text-sm font-medium">Recipients</div>
                </div>

                {/* Product Manager (default on) */}
                <div className="grid grid-cols-[auto_1fr] items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Toggle
                      id="sendToPM"
                      label="Send to Product Manager"
                      enabled={sendToPM}
                      onChange={setSendToPM}
                    />
                    <span className="text-sm">Product Manager (default)</span>
                  </div>
                  <input
                    type="email"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200"
                    placeholder="pm@yourbank.com"
                    value={emailPM}
                    onChange={(e) => setEmailPM(e.target.value)}
                  />
                </div>

                {/* Field BDM */}
                <div className="grid grid-cols-[auto_1fr] items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Toggle
                      id="sendToBDMField"
                      label="Send to Field BDM"
                      enabled={sendToBDMField}
                      onChange={setSendToBDMField}
                    />
                    <span className="text-sm">Field BDM (optional)</span>
                  </div>
                  <input
                    type="email"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200"
                    placeholder="field.bdm@yourbank.com"
                    value={emailBDMField}
                    onChange={(e) => setEmailBDMField(e.target.value)}
                  />
                </div>

                {/* Office BDM */}
                <div className="grid grid-cols-[auto_1fr] items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Toggle
                      id="sendToBDMOffice"
                      label="Send to Office BDM"
                      enabled={sendToBDMOffice}
                      onChange={setSendToBDMOffice}
                    />
                    <span className="text-sm">Office BDM (optional)</span>
                  </div>
                  <input
                    type="email"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200"
                    placeholder="office.bdm@yourbank.com"
                    value={emailBDMOffice}
                    onChange={(e) => setEmailBDMOffice(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleSaveNotifications}
              disabled={savingSettings || loadingSettings}
              className="w-full rounded-xl bg-sky-500 px-4 py-2.5 font-medium text-white shadow hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {savingSettings ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Notification Preferences"
              )}
            </button>
          </div>
        </Card>

        {/* Preferences */}
        <Card className="p-4 sm:p-6">
          <div className="mb-4 flex items-center gap-2">
            <Cog className="h-5 w-5 text-sky-500" />
            <h2 className="text-xl font-semibold">Preferences</h2>
          </div>

          <div className="space-y-5">
            {/* Theme */}
            <div>
              <label className="mb-1 flex items-center gap-2 text-sm font-medium">
                <Cog className="h-4 w-4 text-sky-500" /> Theme
              </label>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">Choose your preferred theme</p>
            </div>

            {/* Language */}
            <div>
              <label className="mb-1 flex items-center gap-2 text-sm font-medium">
                <Globe className="h-4 w-4 text-sky-500" /> Language
              </label>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="en-US">English (US)</option>
                <option value="en-GB">English (UK)</option>
                <option value="fr">French</option>
                <option value="es">Spanish</option>
                <option value="ar">Arabic</option>
              </select>
            </div>

            {/* Time Zone */}
            <div>
              <label className="mb-1 flex items-center gap-2 text-sm font-medium">
                <Clock className="h-4 w-4 text-sky-500" /> Time Zone
              </label>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                <option value="UTC">(UTC+00:00) Coordinated Universal Time</option>
                <option value="Europe/London">(UTC±00:00/01:00) Europe/London</option>
                <option value="Europe/Paris">(UTC+01:00) Europe/Paris</option>
                <option value="America/New_York">(UTC−05:00) America/New_York</option>
                <option value="America/Los_Angeles">(UTC−08:00) America/Los_Angeles</option>
              </select>
            </div>

            <button
              onClick={handleSavePreferences}
              className="w-full rounded-xl bg-sky-500 px-4 py-2.5 font-medium text-white shadow hover:bg-sky-600"
            >
              Save Preferences
            </button>
          </div>
        </Card>

        {/* Security */}
        <Card className="p-4 sm:p-6">
          <div className="mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-sky-500" />
            <h2 className="text-xl font-semibold">Security</h2>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {is2faActive
                ? "Sign-in requires a one-time code sent to your email."
                : isDisableFlow
                  ? "Turn off 2FA by confirming with a one-time code we send to your email."
                  : "Add a verification step to sign-in using a one-time code sent to your email."}
            </p>

            <div
              className={cx(
                "flex items-center justify-between rounded-lg p-4",
                isDisableFlow ? "bg-amber-50 ring-1 ring-amber-200" : "bg-slate-50"
              )}
            >
              <div>
                <p className="font-medium">
                  {is2faActive
                    ? "Email 2FA is on"
                    : isDisableFlow
                      ? "Disable email 2FA"
                      : "Enable email 2FA"}
                </p>
                <p className="text-sm text-gray-600">
                  {is2faActive
                    ? "Turn off the switch to start disable — we will email you a confirmation code."
                    : isDisableFlow
                      ? "Enter the code below to confirm. Until then, 2FA stays on for sign-in."
                      : "You will be asked for an email code after entering your password."}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={twoFaEmailEnabled}
                disabled={twoFaBusy}
                onClick={onTwoFaToggle}
                className={cx(
                  twoFaEmailEnabled ? "bg-sky-500" : "bg-gray-300",
                  "relative inline-flex h-7 w-12 shrink-0 rounded-full border-2 border-transparent transition-colors disabled:opacity-60"
                )}
                aria-label="Toggle email two-factor authentication"
              >
                <span
                  className={cx(
                    twoFaEmailEnabled ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow transition"
                  )}
                />
              </button>
            </div>

            {twoFaMsg && (
              <p
                className={cx(
                  "text-sm font-medium",
                  isDisableFlow ? "text-amber-800" : "text-sky-700"
                )}
              >
                {twoFaMsg}
              </p>
            )}

            {is2faActive ? (
              <p className="text-sm font-semibold text-emerald-700">
                Email 2FA is active for {twoFaEmail || "your account"}.
              </p>
            ) : (
              (twoFaEmailEnabled || setupTmpId) && (
                <div className="grid gap-3">
                  <p className="text-sm text-gray-600">
                    {isDisableFlow
                      ? `Enter the code sent to ${twoFaEmail || "your email"} to disable 2FA.`
                      : `Codes are sent to ${twoFaEmail || "your account email"}.`}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => sendTwoFaCode(isDisableFlow ? "disable" : "enable")}
                      disabled={twoFaBusy}
                      className="rounded-lg bg-slate-900 px-4 py-2 font-medium text-white hover:bg-black disabled:opacity-60"
                    >
                      {twoFaBusy ? "Sending..." : "Resend code"}
                    </button>
                    <span
                      className={cx(
                        "text-sm font-semibold",
                        twoFaPendingAction === "disable"
                          ? "text-amber-700"
                          : twoFaVerified
                            ? "text-emerald-700"
                            : "text-amber-700"
                      )}
                    >
                      {twoFaPendingAction === "disable"
                        ? "Confirm disable"
                        : twoFaVerified
                          ? "Enabled"
                          : "Pending verification"}
                    </span>
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-200"
                    placeholder="Enter code"
                    value={twoFaCode}
                    onChange={(e) => setTwoFaCode(e.target.value.replace(/\s/g, ""))}
                    disabled={twoFaBusy}
                  />
                  <button
                    type="button"
                    onClick={verifyTwoFaCode}
                    disabled={twoFaBusy}
                    className="w-full rounded-lg bg-sky-500 px-4 py-2 font-medium text-white hover:bg-sky-600 disabled:opacity-60"
                  >
                    {twoFaBusy
                      ? "Verifying..."
                      : isDisableFlow
                        ? "Confirm disable"
                        : "Verify code"}
                  </button>
                </div>
              )
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

